import { fetchAllPages } from './crm/CrmApiRepository.js';
import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { CommissionsApiService } from './crm/CommissionsApiService.js';
import { DealsApiService } from './crm/DealsApiService.js';
import {
    mapCommissionFromApi,
    mapCommissionToApi,
    mapCommissionSplitFromApi
} from './crm/ApiEntityMapper.js';
import { nowIso } from '../utils/IdUtils.js';
import { AuditService } from './AuditService.js';
import { ClientService } from './ClientService.js';
import { CommissionRuleService } from './CommissionRuleService.js';
import { DealService } from './DealService.js';
import { UserService } from './UserService.js';
import { ContactService } from './ContactService.js';
import { CacheService } from './CacheService.js';
import { CommissionSplitsApiService } from './crm/CommissionSplitsApiService.js';

const DEFAULT_COMMISSION_RATE = 8;

function invalidateCommissionCache() {
    CacheService.invalidatePattern('^pages:/api/v1/commissions');
}

export class CommissionScheduleService {
    static async resolveEffectiveSplits(deal) {
        if (deal.useAccountCommissionDefaults && deal.clientId) {
            const account = await ClientService.getById(deal.clientId);
            const defaults = ClientService.getCommissionDefaults(account);
            if (defaults.length) return defaults;
        }

        if (Array.isArray(deal.commissionSplits) && deal.commissionSplits.length) {
            return deal.commissionSplits.filter(s => s.userId && Number(s.splitPct) > 0);
        }

        if (deal?.id) {
            try {
                const raw = await DealsApiService.getCommissionSplits(deal.id);
                const rows = Array.isArray(raw) ? raw : (raw?.data ?? []);
                const fromApi = rows.map(mapCommissionSplitFromApi).filter(Boolean)
                    .filter(s => s.userId && Number(s.splitPct) > 0);
                if (fromApi.length) return fromApi;
            } catch (err) {
                console.warn('Failed to load commission splits for deal', deal.id, err);
            }
        }

        return CommissionScheduleService.normalizeSplits(deal);
    }

    static normalizeSplits(deal) {
        if (Array.isArray(deal.commissionSplits) && deal.commissionSplits.length) {
            return deal.commissionSplits.filter(s => (s.userId || s.contactId) && s.splitPct > 0);
        }
        if (deal.ownerUserId) {
            return [{ userId: deal.ownerUserId, recipientType: 'user', splitPct: 100 }];
        }
        return [];
    }

    static async resolveCommissionRate(deal) {
        if (deal?.commissionRate != null && deal.commissionRate !== '') {
            const n = Number(deal.commissionRate);
            if (!Number.isNaN(n) && n > 0) return n;
        }
        if (deal?.ownerUserId) {
            try {
                const users = await UserService.getLookupUsers().catch(() => []);
                const owner = users.find(u => u.id === deal.ownerUserId);
                const pct = Number(owner?.commissionPct);
                if (!Number.isNaN(pct) && pct > 0) return pct;
            } catch {
                /* ignore */
            }
        }
        return DEFAULT_COMMISSION_RATE;
    }

    static computeAccrualAmount(deal, invoicePaidAmount, splitPct, rule = null) {
        const effective = CommissionRuleService.applyRuleToDeal(deal, rule);
        const base = invoicePaidAmount || 0;
        const split = (splitPct || 0) / 100;
        if (effective.commissionModel === 'fixed_amount') {
            const pool = effective.commissionFixedAmount || 0;
            return Math.round(pool * split);
        }
        const rate = effective.commissionRate ?? 0;
        return Math.round(base * (rate / 100) * split);
    }

    static estimateTotalCommission(deal) {
        if (!deal.commissionEligible) return 0;
        const contract = deal.contractValue || deal.dealAmount || deal.amount || 0;
        const splits = CommissionScheduleService.normalizeSplits(deal);
        if (!splits.length) return 0;
        if (deal.commissionModel === 'fixed_amount') {
            return deal.commissionFixedAmount || 0;
        }
        return Math.round(contract * (deal.commissionRate || 0) / 100);
    }

    static async getAll() {
        const rows = await fetchAllPages('/api/v1/commissions');
        return rows.map(mapCommissionFromApi);
    }

    static async getByDeal(dealId) {
        if (!dealId) return [];
        const rows = await fetchAllPages('/api/v1/commissions', { deal_id: dealId });
        return rows.map(mapCommissionFromApi);
    }

    static async getBySchedule(scheduleId) {
        const all = await CommissionScheduleService.getAll();
        return all.filter(r => r.scheduleId === scheduleId);
    }

    static async getByRep(userId) {
        const res = await CommissionsApiService.list({ user_id: userId, per_page: 100 });
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        return rows.map(mapCommissionFromApi);
    }

    static accrualKey(scheduleId, userId) {
        return `${scheduleId || 'none'}:${userId || ''}`;
    }

    static async generateForDeal(deal, schedules = null, { emitEvent = true } = {}) {
        if (!deal?.id || deal.commissionEligible === false) return [];

        // null/undefined → fetch; array (even empty) → use as provided (batch callers).
        let scheds;
        if (Array.isArray(schedules)) {
            scheds = schedules.filter(Boolean);
        } else {
            scheds = [];
            try {
                scheds = await CrmApiRepository.getSchedulesByDeal(deal.id);
            } catch (err) {
                console.warn('Failed to load schedules for commission generation', deal.id, err);
            }
        }

        if (!scheds.length) {
            const contractValue = Number(deal.contractValue || deal.dealAmount || deal.amount || 0);
            if (contractValue <= 0) return [];
            scheds = [{
                id: null,
                plannedAmount: contractValue,
                paidAmount: null,
                status: 'planned',
                invoiceDate: deal.actualCloseDate || deal.expectedCloseDate || null,
                invoiceCode: null
            }];
        }

        const splits = (await CommissionScheduleService.resolveEffectiveSplits(deal))
            .filter(s => s.userId && Number(s.splitPct) > 0);
        if (!splits.length) return [];

        const rate = await CommissionScheduleService.resolveCommissionRate(deal);
        const dealForCalc = {
            ...deal,
            commissionRate: rate,
            commissionModel: deal.commissionModel || 'percentage'
        };

        const existing = await CommissionScheduleService.getByDeal(deal.id);
        const existingKeys = new Set(
            existing.map(r => CommissionScheduleService.accrualKey(r.scheduleId, r.ownerUserId || r.userId))
        );

        const created = [];
        for (const schedule of scheds) {
            if (schedule.status === 'cancelled') continue;
            const baseAmount = Number(
                schedule.status === 'paid'
                    ? (schedule.paidAmount ?? schedule.actualAmount ?? schedule.plannedAmount ?? 0)
                    : (schedule.plannedAmount ?? schedule.amount ?? 0)
            );
            for (const split of splits) {
                const key = CommissionScheduleService.accrualKey(schedule.id, split.userId);
                if (existingKeys.has(key)) continue;

                const amount = CommissionScheduleService.computeAccrualAmount(
                    dealForCalc,
                    baseAmount,
                    split.splitPct
                );
                const status = schedule.status === 'paid' ? 'paid' : 'pending';
                try {
                    const payload = mapCommissionToApi({
                        dealId: deal.id,
                        scheduleId: schedule.id || null,
                        ownerUserId: split.userId,
                        amount,
                        status,
                        rate,
                        relation: split.relation || null,
                        notes: null
                    });
                    const row = await CommissionsApiService.create(payload);
                    const mapped = mapCommissionFromApi(row);
                    if (mapped) {
                        existingKeys.add(key);
                        created.push(mapped);
                    }
                } catch (err) {
                    console.warn('Failed to create commission accrual', { dealId: deal.id, key }, err);
                }
            }
        }

        if (created.length) {
            invalidateCommissionCache();
            if (emitEvent) {
                window.dispatchEvent(new CustomEvent('commissionUpdated', {
                    detail: { commissions: created, dealId: deal.id }
                }));
            }
        }
        return created;
    }

    static async accrueOnInvoicePaid(schedule) {
        if (!schedule?.dealId) return [];

        const existing = await CommissionScheduleService.getBySchedule(schedule.id);
        if (existing.length) {
            const updated = [];
            for (const row of existing) {
                if (row.status === 'paid') {
                    updated.push(row);
                    continue;
                }
                const next = await CommissionScheduleService.updateStatus(row.id, 'paid', {
                    paidDate: nowIso().split('T')[0]
                });
                if (next) updated.push(next);
            }
            return updated;
        }

        let deal = null;
        try {
            deal = await DealService.getById(schedule.dealId);
        } catch (err) {
            console.warn('Failed to load deal for commission accrual', schedule.dealId, err);
            return [];
        }
        if (!deal || deal.commissionEligible === false) return [];
        return CommissionScheduleService.generateForDeal(deal, [schedule]);
    }

    static async ensureAccrualsForClosedWonDeals() {
        const deals = await DealService.getAll();
        const won = deals.filter(d => d.status === 'won' || d.dealStage === 'closed');
        if (!won.length) return [];

        const existing = await CommissionScheduleService.getAll();
        const dealsWithAccruals = new Set(existing.map(r => r.dealId).filter(Boolean));
        // Only deals that are missing accruals entirely — skip when nothing to create.
        const missing = won.filter(d => d?.id && !dealsWithAccruals.has(d.id));
        if (!missing.length) return [];

        const [allSchedules, splitsByDeal] = await Promise.all([
            CrmApiRepository.getAllSchedules().catch(() => []),
            CommissionSplitsApiService.getBatch(missing.map(d => d.id)).catch(() => ({}))
        ]);

        const schedulesByDeal = new Map();
        for (const schedule of allSchedules || []) {
            const dealId = schedule.dealId;
            if (!dealId) continue;
            const list = schedulesByDeal.get(dealId) || [];
            list.push(schedule);
            schedulesByDeal.set(dealId, list);
        }

        const created = [];
        for (const deal of missing) {
            try {
                const apiSplits = splitsByDeal?.[deal.id] || splitsByDeal?.[String(deal.id)] || [];
                const dealWithSplits = {
                    ...deal,
                    commissionSplits: (Array.isArray(apiSplits) ? apiSplits : [])
                        .map(mapCommissionSplitFromApi)
                        .filter(Boolean)
                };
                const rows = await CommissionScheduleService.generateForDeal(
                    dealWithSplits,
                    schedulesByDeal.get(deal.id) || [],
                    { emitEvent: false }
                );
                if (rows.length) {
                    dealsWithAccruals.add(deal.id);
                    created.push(...rows);
                }
            } catch (err) {
                console.warn('Commission backfill failed for deal', deal.id, err);
            }
        }
        return created;
    }

    static async updateStatus(id, status, { paidDate = null, payoutDate = null, overrideNotes = null } = {}) {
        const all = await CommissionScheduleService.getAll();
        const row = all.find(r => r.id === id);
        if (!row) return null;

        const payload = { status };
        if (overrideNotes != null) payload.notes = overrideNotes;
        if (status === 'paid' && paidDate) payload.paid_date = paidDate;

        let updatedRemote = null;
        try {
            updatedRemote = await CommissionsApiService.update(id, payload);
        } catch (err) {
            console.warn('Commission status API update failed; applying local event only', err);
        }

        const updated = {
            ...(mapCommissionFromApi(updatedRemote) || row),
            status,
            paidDate: paidDate ?? row.paidDate,
            payoutDate: payoutDate ?? row.payoutDate,
            overrideNotes: overrideNotes != null ? overrideNotes : row.overrideNotes,
            updatedAt: nowIso()
        };
        invalidateCommissionCache();
        await AuditService.log({
            entityType: 'commission',
            entityId: id,
            action: 'commission.updated',
            before: { status: row.status },
            after: { status }
        });
        window.dispatchEvent(new CustomEvent('commissionUpdated', { detail: { commission: updated } }));
        return updated;
    }

    static async save(row) {
        if (!row?.id) return row;
        try {
            const payload = {
                amount: row.amount,
                status: row.status,
                notes: row.overrideNotes ?? row.notes ?? null
            };
            if (row.rate != null) payload.rate = Number(row.rate);
            if (row.paidDate) payload.paid_date = row.paidDate;
            const updated = await CommissionsApiService.update(row.id, payload);
            const mapped = mapCommissionFromApi(updated) || row;
            invalidateCommissionCache();
            window.dispatchEvent(new CustomEvent('commissionUpdated', { detail: { commission: mapped } }));
            return mapped;
        } catch (err) {
            console.warn('Commission save failed', err);
            window.dispatchEvent(new CustomEvent('commissionUpdated', { detail: { commission: row } }));
            return row;
        }
    }

    static async getEnrichedAll() {
        const rows = await CommissionScheduleService.getAll();
        return rows.map(r => {
            const invoiceAmount = r.invoice_amount != null
                ? Number(r.invoice_amount)
                : Number(r.invoiceAmount ?? r.amount ?? 0);
            const commissionRate = r.commission_rate != null
                ? Number(r.commission_rate)
                : (r.rate != null ? Number(r.rate) : DEFAULT_COMMISSION_RATE);
            return {
                ...r,
                clientId: r.clientId || r.client_id || null,
                dealName: r.deal_name || r.dealName || r.dealId,
                accountName: r.account_name || r.accountName || '—',
                repName: r.rep_name || r.repName || '—',
                recipientType: r.contactId && !r.ownerUserId ? 'contact' : 'user',
                dealCommissionEligible: r.deal_commission_eligible !== false,
                commissionIneligibleSince: r.commissionIneligibleSince || null,
                invoiceAmount,
                invoiceCode: r.invoice_code || r.invoiceCode || '—',
                invoiceDate: r.invoice_date || r.invoiceDate || (r.createdAt ? String(r.createdAt).slice(0, 10) : null),
                commissionRate: Number(commissionRate) || 0
            };
        });
    }

    static groupByAccount(rows) {
        const groups = {};
        for (const row of rows) {
            const key = row.clientId || 'unknown';
            if (!groups[key]) {
                groups[key] = {
                    clientId: key,
                    accountName: row.accountName || '—',
                    rows: []
                };
            }
            groups[key].rows.push(row);
        }
        return Object.values(groups).sort((a, b) => a.accountName.localeCompare(b.accountName));
    }

    static accountSummary(rows) {
        const paid = rows.filter(r => r.status === 'paid').reduce((s, r) => s + (r.amount || 0), 0);
        const pending = rows.filter(r => r.status === 'pending' || r.status === 'approved')
            .reduce((s, r) => s + (r.amount || 0), 0);
        const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
        const invoiceCodes = new Set(rows.map(r => r.scheduleId || r.invoiceCode).filter(Boolean));
        return { paid, pending, total, invoiceCount: invoiceCodes.size || rows.length };
    }

    static async bulkUpdateStatus(ids, status, opts = {}) {
        const results = [];
        for (const id of ids) {
            const updated = await CommissionScheduleService.updateStatus(id, status, opts);
            if (updated) results.push(updated);
        }
        return results;
    }

    static sumByStatus(rows, statuses) {
        const set = new Set(statuses);
        return rows.filter(r => set.has(r.status)).reduce((s, r) => s + (r.amount || 0), 0);
    }

    static paidInYear(rows, userId, year) {
        return rows
            .filter(r => r.ownerUserId === userId && r.status === 'paid')
            .filter(r => {
                const d = r.paidDate || r.invoiceDate || r.createdAt;
                return d && new Date(d).getFullYear() === year;
            })
            .reduce((s, r) => s + (r.amount || 0), 0);
    }

    static filterRows(rows, { year, userId, clientId, month, status } = {}) {
        return rows.filter(r => {
            if (userId && r.ownerUserId !== userId) return false;
            if (clientId && r.clientId !== clientId) return false;
            if (status && r.status !== status) return false;
            if (year) {
                const d = r.paidDate || r.invoiceDate || r.createdAt;
                if (!d || new Date(d).getFullYear() !== Number(year)) return false;
            }
            if (month) {
                const d = r.paidDate || r.invoiceDate || r.createdAt;
                if (!d || (new Date(d).getMonth() + 1) !== Number(month)) return false;
            }
            return true;
        });
    }
}
